import { Alert, Button, SpaceBetween, Spinner, Toggle } from '@amzn/awsui-components-core';
import {
  ContentWorkflow,
  PublicationFeedback,
  PublicationFeedbackState,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import constants from '../../common/constants';
import PublicationFeedbackListItem, { FeedbackUpdate } from './PublicationFeedbackListItem';
import { ContentViewerContext } from '../workflow-viewer/ContentViewerContext';
import { getUserRoles } from '../../api/UtilsApi';
import { DateTime } from 'luxon';
import { AppContext } from '../../contexts/AppContext';
import UpdateRequestFeedbackForm from './UpdateRequestFeedbackForm';
import {
  publicationFeedbackTypeDisplayName,
  PublicationFeedbackTypeSeverity,
  isFeedbackClosed,
} from '../utils/DisplayNames';
import AuthorActionsDropdown from './AuthorActionsDropdown';

interface Props {
  contentId: string;
  ownerId: string;
  onFeedbacksUpdated: (publicationFeedbacks: PublicationFeedback[]) => void;
  initialShowFeedbackForm?: boolean;
  highlightFeedbackId?: string;
}

const OLD_REQUEST_THRESHOLD_DAYS = 21;

const mplServiceClient = MplServiceClientFactory.create();

// Severity priority for sorting (higher number = higher priority)
const severityPriority: Record<PublicationFeedbackTypeSeverity, number> = {
  High: 3,
  Medium: 2,
  Low: 1,
  NA: 0,
};

const getSeverityForFeedback = (feedback: PublicationFeedback): PublicationFeedbackTypeSeverity => {
  const feedbackType = publicationFeedbackTypeDisplayName.get(
    // @ts-expect-error TS2345 - strict migration
    feedback.publicationFeedbackData?.issueType,
  );
  return feedbackType?.severity ?? 'NA';
};

const sortFeedbacksBySeverityAndTime = (
  feedbacks: PublicationFeedback[],
): PublicationFeedback[] => {
  return [...feedbacks].sort((a, b) => {
    // First sort by severity (High > Medium > Low > NA)
    const severityA = severityPriority[getSeverityForFeedback(a)];
    const severityB = severityPriority[getSeverityForFeedback(b)];
    if (severityA !== severityB) {
      return severityB - severityA;
    }
    // Then sort by timestamp (most recent first)
    const timeA = a.lastUpdatedAt?.toMillis() ?? 0;
    const timeB = b.lastUpdatedAt?.toMillis() ?? 0;
    return timeB - timeA;
  });
};

const UpdateRequestsPage = ({
  contentId,
  ownerId,
  onFeedbacksUpdated,
  initialShowFeedbackForm,
  highlightFeedbackId,
}: Props) => {
  const [publicationFeedbacks, setPublicationFeedbacks] = useState<PublicationFeedback[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(initialShowFeedbackForm || false);
  const [workflow, setWorkflow] = useState(new ContentWorkflow());
  const loadRequestIdRef = useRef(0);

  const { owner, coAuthors } = useContext(ContentViewerContext);
  const { authnInfo } = useContext(AppContext);
  const { isOwner, isCoAuthors, isPrivilegedUser } = authnInfo?.userProfile
    ? getUserRoles(authnInfo.userProfile, {
        ownerId: owner?.userId,
        coAuthorIds: coAuthors?.map((co) => co.userId),
      })
    : { isOwner: false, isCoAuthors: false, isPrivilegedUser: false };

  const isAuthorOrPrivileged = isOwner || isCoAuthors || isPrivilegedUser;

  const loadUpdateRequests = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;

    try {
      setIsLoading(true);
      const { publicationFeedbacks: pFeedbacks } =
        await mplServiceClient.listPublicationFeedbacks(contentId);

      // Ignore stale responses
      if (requestId !== loadRequestIdRef.current) return;

      const sortedFeedbacks = sortFeedbacksBySeverityAndTime(pFeedbacks);
      setPublicationFeedbacks(sortedFeedbacks);
      onFeedbacksUpdated(sortedFeedbacks);
      if (isAuthorOrPrivileged) {
        const { workflow: contentWorkflow } = await mplServiceClient.getContentWorkflow(contentId);

        // Check again after second async call
        if (requestId !== loadRequestIdRef.current) return;

        setWorkflow(contentWorkflow);
      }
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [contentId, onFeedbacksUpdated, isAuthorOrPrivileged]);

  useEffect(() => {
    loadUpdateRequests();
  }, [loadUpdateRequests]);

  // Patches both `state` and `activities` on the feedback object in parent state.
  // Previously only `state` was patched, which meant the activities array held by this
  // component was stale. When PublicationFeedbackListItem remounted (e.g., after toggling
  // "Show closed requests" or list reordering from severity sort), its `localActivities`
  // would reinitialize from the stale prop and the most recent activity would be missing —
  // requiring a full page refresh to see it. Appending the activity here keeps the parent's
  // state in sync so remounts always have the latest data.
  // Handles both status changes (newState present) and comment-only updates (newState absent).
  const handleFeedbackUpdate = useCallback(
    (update: FeedbackUpdate) => {
      setPublicationFeedbacks((prevFeedbacks) => {
        const updatedFeedbacks = prevFeedbacks.map((pf) => {
          if (pf.feedbackId !== update.feedbackId) return pf;
          const updatedFeedback = { ...pf };
          if (update.newState) {
            updatedFeedback.state = update.newState;
          }
          if (update.activity) {
            updatedFeedback.activities = [...(pf.activities ?? []), update.activity];
          }
          return updatedFeedback;
        });
        onFeedbacksUpdated(updatedFeedbacks);
        return updatedFeedbacks;
      });
    },
    [onFeedbacksUpdated],
  );

  const getPendingFeedbacks = () => {
    return publicationFeedbacks.filter((pf) => !isFeedbackClosed(pf.state));
  };

  // Show actions dropdown always for authors/co-authors/admins/TWs
  // Also show for anyone when an open update request is over 21 days old
  const showActionsDropdown = () => {
    return isAuthorOrPrivileged || hasOldOpenRequest();
  };

  const hasOldOpenRequest = () => {
    return publicationFeedbacks.some((pf) => {
      if (pf.state !== PublicationFeedbackState.CREATED) return false;
      const createdAt = pf.createdAt;
      if (!createdAt) return false;
      const daysSinceCreated =
        DateTime.utc().diff(createdAt, ['days', 'hours']).toObject().days ?? 0;
      return daysSinceCreated > OLD_REQUEST_THRESHOLD_DAYS;
    });
  };

  // @ts-expect-error TS7031 - strict migration
  const onShowResolved = ({ detail }) => {
    setShowResolved(detail.checked);
  };

  if (isLoading) {
    return <Spinner size="normal" />;
  }

  const renderNoPendingRequestInfo = () => {
    if (!feedbackItems.length) {
      const message = showResolved
        ? constants.NO_OPEN_OR_CLOSED_FEEDBACK
        : constants.NO_OPEN_FEEDBACK;
      return <Alert type="info">{message}</Alert>;
    }
    return null;
  };

  const onComplete = () => {
    window.location.reload();
  };

  const feedbackItems = showResolved ? publicationFeedbacks : getPendingFeedbacks();
  const feedbackList = feedbackItems.length
    ? feedbackItems.map((pf) => (
        <PublicationFeedbackListItem
          key={pf.feedbackId}
          publicationFeedback={pf}
          contentId={contentId}
          ownerId={ownerId}
          onUpdate={handleFeedbackUpdate}
          highlight={pf.feedbackId === highlightFeedbackId}
        />
      ))
    : null;
  if (showFeedbackForm) {
    return (
      <UpdateRequestFeedbackForm
        contentId={contentId}
        onCancel={() => setShowFeedbackForm(false)}
        onSubmitSuccess={() => {
          setShowFeedbackForm(false);
          loadUpdateRequests();
        }}
      />
    );
  }

  return isLoading ? (
    <Spinner />
  ) : (
    <SpaceBetween direction="vertical" size="l">
      {/* Top row: Toggle and Buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Toggle checked={showResolved} onChange={onShowResolved}>
          Show closed requests
        </Toggle>
        <SpaceBetween direction="horizontal" size="xs">
          {showActionsDropdown() && (
            <AuthorActionsDropdown
              contentId={contentId}
              ownerId={ownerId}
              workflow={workflow}
              // @ts-expect-error TS2322 - strict migration
              isAuthor={isOwner || isCoAuthors}
              isAuthorOrPrivileged={isAuthorOrPrivileged}
              onComplete={onComplete}
            />
          )}
          <Button variant="primary" onClick={() => setShowFeedbackForm(true)}>
            Provide feedback
          </Button>
        </SpaceBetween>
      </div>
      {/* Content: Feedback items */}
      <SpaceBetween direction="vertical" size="m">
        {feedbackList}
        {renderNoPendingRequestInfo()}
      </SpaceBetween>
    </SpaceBetween>
  );
};

export default UpdateRequestsPage;
